import { useRef, useCallback, useEffect, useState } from 'react';
import { cssVar, HStack, Box, Icon, Text, Input, Flex } from '@cad-arc';
import { observer } from 'mobx-react-lite';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { CommandLineEditorStore } from './command-line-editor-store';
import { HelpObject } from './helpers';
import { sortBy } from 'lodash';
import isHotKey from 'is-hotkey';

function HelpRow({
    onClick,
    onMouseEnter,
    item,
    isSelected,
}: {
    onClick: () => void;
    onMouseEnter: () => void;
    item: HelpObject;
    isSelected: boolean;
}): React.ReactElement {
    const color = isSelected
        ? cssVar('arc.colors.selected.content')
        : cssVar('arc.colors.text.primary');

    return (
        <HStack
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            cursor="pointer"
            w="full"
            alignItems="center"
            h="85px"
            bg={isSelected ? 'selected.default' : ''}
            borderBottom="md"
            borderColor="border.default"
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                h="full"
                textAlign="center"
                w="60px"
            >
                <Text variant="caption" fontWeight="bold" style={{ color }}>
                    {item.cliArgumentConfigurationView.argumentDefinition}
                </Text>
            </Box>
            <Box>
                <Text variant="caption" style={{ color }}>
                    {item.cliArgumentConfigurationView.title}
                </Text>
                <Text variant="caption" style={{ color }}>
                    {item.cliArgumentConfigurationView.description}
                </Text>
                <Text variant="caption" style={{ color }}>
                    Eg.: {item.exampleText}
                </Text>
            </Box>
        </HStack>
    );
}

export const HelpWindow = observer(function HelpWindow(props: {
    editorStore: CommandLineEditorStore;
    historyHeight: number;
}): React.ReactElement {
    const { editorStore } = props;
    const [query, setQuery] = useState<string>();
    const [selectedIndex, setSelectedIndex] = useState<number>();
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const boxRef = useRef<HTMLDivElement>(null);

    const renderItemContent = (index: number): React.ReactElement => {
        const item = rows[index];
        return (
            <HelpRow
                onClick={() => editorStore.selectHelpCommand(item)}
                onMouseEnter={() => setSelectedIndex(index)}
                item={item}
                isSelected={index === selectedIndex}
            />
        );
    };

    const rows = sortBy(
        editorStore.config.helpText.slice().filter((helpObject) => {
            return helpObject.searchText.includes(query ? query.toLowerCase() : '');
        }),
        (item) => item.cliArgumentConfigurationView.argumentDefinition
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (isHotKey('Down', e)) {
                if (typeof selectedIndex === 'undefined') {
                    setSelectedIndex(0);
                } else if (selectedIndex < rows.length - 1) {
                    setSelectedIndex(selectedIndex + 1);
                }
            } else if (isHotKey('Up', e)) {
                if (selectedIndex && selectedIndex > 0) {
                    setSelectedIndex(selectedIndex - 1);
                }
            } else if (isHotKey('Esc', e)) {
                editorStore.escapeHelpWindow();
            } else if (isHotKey('Enter', e)) {
                if (typeof selectedIndex !== 'undefined' && rows[selectedIndex]) {
                    editorStore.selectHelpCommand(rows[selectedIndex]);
                }
            }
        },
        [editorStore, selectedIndex, setSelectedIndex, rows]
    );

    useEffect(() => {
        if (virtuosoRef.current) {
            if (typeof selectedIndex === 'undefined') {
                virtuosoRef.current.scrollIntoView({ index: 0, behavior: 'auto' });
            } else {
                virtuosoRef.current.scrollIntoView({ index: selectedIndex, behavior: 'auto' });
            }
        }
    }, [selectedIndex]);

    return (
        <Flex
            direction="column"
            ref={boxRef}
            className="help-window"
            bg="surface.canvas"
            position="relative"
            transform="translateX(-50%)"
            left="50%"
            w="92%"
            h={props.historyHeight + 2}
            minH="500px"
            overflow="hidden"
            pb="36px"
            borderRadius="base"
            border="md"
            borderColor="var(--cad-colors-command-line-popout-border)"
        >
            <Box p="4">
                <Input
                    autoFocus
                    onMouseDown={(e: React.MouseEvent<HTMLInputElement>) => {
                        e.stopPropagation();
                    }}
                    type="text"
                    isClearable={true}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        handleKeyDown(e);
                    }}
                    leftElement={
                        <Icon icon="Search" color="brand.default" />
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSelectedIndex(0);
                        setQuery(e.currentTarget.value);
                    }}
                    role="textbox"
                />
            </Box>

            <Virtuoso
                style={{flex: 1}}
                tabIndex={-1}
                ref={virtuosoRef}
                totalCount={rows.length}
                overscan={10}
                itemContent={renderItemContent}
            />
        </Flex>
    );
});
